// L'ASSISTANT DE RYTHME — les commandes qui composent une planification, et l'aperçu de ce qu'elle fera.
//
// ⚠️ EXTRAIT DE `Parametres.tsx` PARCE QU'IL SERT MAINTENANT À DEUX ENDROITS : le rythme COMMUN (Paramètres
// → Planifications) et le rythme PROPRE d'un fournisseur (fiche fournisseur → Relève). Recopié, les deux
// écrans auraient fini par proposer des réglages différents pour la même chose — et c'est l'un d'eux qui
// décide de l'heure à laquelle on va chercher les fichiers.
//
// ⚠️ IL NE SAIT NI ENREGISTRER NI OÙ IL EST : il rend une valeur et signale ses changements. C'est
// l'appelant qui décide de la route (globale ou par fournisseur), du bouton et du message. Sans cette
// séparation, l'assistant devrait connaître ses deux contextes, donc porter un `if` sur chacun.
//
// ⚠️ L'APERÇU « CE QUI SE PASSERA » VIENT DE `schedCalc`, LA MÊME FONCTION QUI VALIDE — ils ne peuvent donc
// pas diverger. Un aperçu calculé à part finirait par annoncer autre chose que ce qui est enregistré (§23.0).
import { schedCalc, type SchedMode, type SchedVals } from '../sched';
import { useId } from 'react';

const lab = { fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase' as const, color: 'var(--ink-500)', display: 'block', marginBottom: 5 };
const inputSt = { width: '100%', height: 38, padding: '0 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)' } as const;
const DAYS = [[1, 'L'], [2, 'M'], [3, 'M'], [4, 'J'], [5, 'V'], [6, 'S'], [7, 'D']] as const;

export function RhythmAssistant({ value, onChange }: { value: SchedVals; onChange: (v: SchedVals) => void }) {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const v = value;
  const r = schedCalc(v);
  const set = <K extends keyof SchedVals>(k: K, val: SchedVals[K]) => onChange({ ...v, [k]: val });
  const toggleDay = (d: number) => set('days', v.days.includes(d) ? v.days.filter((x) => x !== d) : [...v.days, d].sort((a, b) => a - b));
  const toggleHour = (h: number) => set('hours', v.hours.includes(h) ? v.hours.filter((x) => x !== h) : [...v.hours, h].sort((a, b) => a - b));

  return (
    <>
      <div className="seg-track" style={{ marginBottom: 16 }}>
        {([['interval', 'Toutes les X h'], ['fixed', 'À heures fixes'], ['cron', 'Expression cron']] as [SchedMode, string][]).map(([id, label]) => (
          <button key={id} type="button" onClick={() => set('mode', id)} className={v.mode === id ? 'seg active' : 'seg'}>{label}</button>
        ))}
      </div>

      {v.mode === 'interval' && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          <div><label htmlFor={`${fieldId}-0`} style={lab}>Fréquence</label><select id={`${fieldId}-0`} style={{ ...inputSt, width: 130 }} value={v.every} onChange={(e) => set('every', Number(e.target.value))}>{[1, 2, 3, 4, 6].map((n) => <option key={n} value={n}>toutes les {n} h</option>)}</select></div>
          <div><label htmlFor={`${fieldId}-1`} style={lab}>De</label><input id={`${fieldId}-1`} type="time" style={{ ...inputSt, width: 120 }} value={v.from} onChange={(e) => set('from', e.target.value)} /></div>
          <div><label htmlFor={`${fieldId}-2`} style={lab}>À</label><input id={`${fieldId}-2`} type="time" style={{ ...inputSt, width: 120 }} value={v.to} onChange={(e) => set('to', e.target.value)} /></div>
        </div>
      )}
      {v.mode === 'fixed' && (
        <div style={{ marginBottom: 16 }}>
          <span id={`${fieldId}-hours`} style={lab}>Heures de déclenchement</span>
          <div role="group" aria-labelledby={`${fieldId}-hours`} style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}>
            {Array.from({ length: 24 }, (_, h) => (
              <button key={h} type="button" onClick={() => toggleHour(h)} style={{ height: 30, border: `1px solid ${v.hours.includes(h) ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontWeight: 600, fontVariantNumeric: 'tabular-nums', background: v.hours.includes(h) ? 'var(--teal-100)' : 'var(--paper)', color: v.hours.includes(h) ? 'var(--teal-700)' : 'var(--ink-500)', cursor: 'pointer' }}>{String(h).padStart(2, '0')}</button>
            ))}
          </div>
        </div>
      )}
      {v.mode === 'cron' && (
        <div style={{ marginBottom: 16 }}>
          <label htmlFor={`${fieldId}-3`} style={lab}>Expression cron (min heure * * jours)</label>
          <input id={`${fieldId}-3`} style={{ ...inputSt, fontFamily: 'var(--font-mono)' }} value={v.cron} onChange={(e) => set('cron', e.target.value)} placeholder="0 7,11,15,19 * * 1-5" />
        </div>
      )}

      {v.mode !== 'cron' && (
        <div style={{ marginBottom: 16 }}>
          <span id={`${fieldId}-days`} style={lab}>Jours</span>
          <div role="group" aria-labelledby={`${fieldId}-days`} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {DAYS.map(([d, l]) => (
              <button key={d} type="button" onClick={() => toggleDay(d)} style={{ width: 34, height: 34, borderRadius: 'var(--r-xs)', border: `1px solid ${v.days.includes(d) ? 'var(--teal-500)' : 'var(--ink-200)'}`, background: v.days.includes(d) ? 'var(--teal-100)' : 'var(--paper)', color: v.days.includes(d) ? 'var(--teal-700)' : 'var(--ink-500)', fontSize: 'var(--t-body)', fontWeight: 600, cursor: 'pointer' }}>{l}</button>
            ))}
            <span style={{ width: 1, height: 22, background: 'var(--ink-200)', margin: '0 4px' }} />
            <button type="button" onClick={() => set('days', [1, 2, 3, 4, 5])} className="chip">Jours ouvrés</button>
            <button type="button" onClick={() => set('days', [1, 2, 3, 4, 5, 6, 7])} className="chip">Tous les jours</button>
          </div>
        </div>
      )}

      <div style={{ borderRadius: 'var(--r-sm)', border: `1px solid ${r.ready ? 'var(--ink-200)' : 'var(--rose-bg)'}`, background: r.ready ? 'var(--ink-50)' : 'var(--rose-bg)', padding: 14 }}>
        <div style={{ fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 6 }}>Ce qui se passera</div>
        {r.ready ? (
          <>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)' }}>{r.summary}</div>
            {r.cronRead && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', marginTop: 3 }}>{r.cronRead}</div>}
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>{r.detail}</div>
          </>
        ) : (
          <div style={{ fontSize: 'var(--t-lead)', color: 'var(--rose-fg)', fontWeight: 500 }}>{r.problem}</div>
        )}
      </div>
    </>
  );
}
